import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Trash2, Clock, Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function useTimeRemaining(t) {
  return (expiresAt) => {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt).getTime() - Date.now();
    if (ms <= 0) return t("usims.expired");
    const hours = Math.floor(ms / 3600000);
    if (hours >= 24) return `${Math.floor(hours / 24)}z ${hours % 24}h`;
    if (hours >= 1) return `${hours}h`;
    return `${Math.max(1, Math.floor(ms / 60000))}m`;
  };
}

export default function UserSimulationsList({ isStaff }) {
  const { t } = useTranslation();
  const timeRemaining = useTimeRemaining(t);
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const STATUS_LABEL = {
    pending: t("usims.status_pending"),
    generating: t("usims.status_generating"),
    completed: t("usims.status_completed"),
    failed: t("usims.status_failed"),
  };

  const fetchSims = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/simulations");
      setSims(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("usims.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSims();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/simulations/${confirmDelete.id}`);
      setSims((s) => s.filter((x) => x.id !== confirmDelete.id));
      toast.success(t("usims.delete_success"));
      setConfirmDelete(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("usims.delete_error"));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={24} />
      </div>
    );
  }

  if (sims.length === 0) {
    return (
      <div className="text-center py-10 text-zinc-500 text-sm" data-testid="user-sims-empty">
        {t("usims.empty")}
      </div>
    );
  }

  return (
    <div data-testid="user-sims-list">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-outfit font-black text-white text-2xl tracking-tight">
          {t("usims.title")}
        </h3>
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
          {isStaff
            ? t(sims.length === 1 ? "usims.subtitle_staff" : "usims.subtitle_staff_plural", { count: sims.length })
            : t("usims.subtitle_user")}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {sims.map((sim) => {
          const imgs = sim.generated_images || [];
          const thumb = imgs[0]?.url;
          const status = sim.status || "pending";
          const remaining = isStaff ? null : timeRemaining(sim.expires_at);
          const expiring = remaining && remaining !== t("usims.expired") && /^\d+m$|^\d+h$/.test(remaining);
          return (
            <div
              key={sim.id}
              className="relative group border border-white/10 rounded-lg overflow-hidden bg-zinc-900/40 hover:border-orange-500/40 transition"
              data-testid={`user-sim-card-${sim.id}`}
            >
              <Link to={`/simulation/${sim.id}`} className="block">
                <div className="aspect-[4/3] bg-zinc-950 relative overflow-hidden">
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={sim.style}
                      onError={(e) => {
                        // Gracefully hide broken thumbnails so the alt-text
                        // doesn't overlap the status badge in the top-left.
                        e.currentTarget.style.display = "none";
                      }}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-600">
                      {status === "generating" ? (
                        <Loader2 className="animate-spin" size={28} />
                      ) : (
                        <AlertCircle size={28} />
                      )}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-black/70 border border-white/10 text-[10px] uppercase tracking-wider text-white">
                    {status === "completed" ? (
                      <CheckCircle2 size={10} className="text-emerald-400" />
                    ) : status === "generating" || status === "pending" ? (
                      <Loader2 size={10} className="animate-spin text-orange-400" />
                    ) : (
                      <AlertCircle size={10} className="text-red-400" />
                    )}
                    {STATUS_LABEL[status] || status}
                  </div>
                  {imgs.length > 0 && (
                    <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-white border border-white/10">
                      {imgs.length} {imgs.length === 1 ? t("usims.variation_singular") : t("usims.variation_plural")}
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-zinc-500 uppercase tracking-wider">
                        {sim.sim_type}
                      </div>
                      <div className="text-sm text-white font-medium mt-0.5">
                        {sim.style?.replace("_", " ")}
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-zinc-500 group-hover:text-orange-400 group-hover:translate-x-1 transition" />
                  </div>
                  {remaining && (
                    <div
                      className={`mt-2 flex items-center gap-1 text-[11px] ${
                        expiring ? "text-amber-400" : "text-zinc-500"
                      }`}
                    >
                      <Clock size={10} />
                      <span>{remaining === t("usims.expired") ? t("usims.expired") : t("usims.expires_in", { time: remaining })}</span>
                    </div>
                  )}
                </div>
              </Link>
              {/* Delete button (always available; backend enforces rules) */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmDelete(sim);
                }}
                className="absolute top-2 right-2 w-7 h-7 rounded bg-black/70 border border-white/15 text-zinc-300 hover:bg-red-500/90 hover:text-white hover:border-red-400 transition flex items-center justify-center"
                title={t("usims.delete_btn_title")}
                data-testid={`user-sim-delete-${sim.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          );
        })}
      </div>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !deleting && !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-zinc-950 border-white/10" data-testid="delete-sim-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t("usims.delete_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("usims.delete_dialog_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={deleting}
              className="bg-transparent border-white/15 text-zinc-300 hover:bg-white/5 hover:text-white"
              data-testid="delete-sim-cancel"
            >
              {t("usims.delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="delete-sim-confirm"
            >
              {deleting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
              {t("usims.delete_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
